/**
 * ConsentSignPage — Öffentliche Unterschrift-Seite per Token-Link (/einverstaendnis/:token).
 * Kunden ohne Portal-Login (z.B. nach Chat-Widget- oder WhatsApp-Buchung) können
 * hier die Einverständnis-Erklärungen (Kryosauna / EMS) digital unterschreiben.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { FileSignature, CheckCircle2, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ConsentSignSection from '@/components/portal/ConsentSignSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ConsentSignPage = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  // ?form=ems|kryo → nur das relevante Formular anzeigen (z.B. EMS-Buchung → nur EMS)
  const filterFormId = searchParams.get('form');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [openFormId, setOpenFormId] = useState(null);
  const [payload, setPayload] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/public/consent-sign/${token}`);
      setData(res.data);
      const forms = res.data.forms || [];
      const relevant = filterFormId && forms.some(f => f.id === filterFormId)
        ? forms.filter(f => f.id === filterFormId)
        : forms;
      const firstUnsigned = relevant.find(f => !f.signed);
      setOpenFormId(firstUnsigned?.id || null);
    } catch (e) {
      setError(e.response?.status === 404
        ? 'Dieser Link ist ungültig oder abgelaufen. Bitte kontaktiere uns.'
        : 'Fehler beim Laden. Bitte versuche es später erneut.');
    }
  }, [token, filterFormId]);

  useEffect(() => { load(); }, [load]);

  const submit = async (formId) => {
    if (!payload || payload.form_id !== formId) {
      toast.error('Bitte alles ausfüllen und unterschreiben (Checkliste, Erklärung, Unterschrift)');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/consent-sign/${token}`, payload);
      toast.success('Einverständnis unterschrieben ✅');
      setPayload(null);
      load();
    } catch (e) {
      const det = e.response?.data?.detail;
      toast.error(typeof det === 'string' ? det : 'Fehler beim Speichern');
    } finally {
      setSubmitting(false);
    }
  };

  const allForms = data?.forms || [];
  const visibleForms = filterFormId && allForms.some(f => f.id === filterFormId)
    ? allForms.filter(f => f.id === filterFormId)
    : allForms;
  const isFiltered = visibleForms.length === 1 && allForms.length > 1;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white py-8 px-4">
      <Toaster position="top-center" richColors />
      <div className="max-w-2xl mx-auto space-y-4">
        <div className="text-center mb-6">
          <FileSignature className="w-10 h-10 text-yellow-400 mx-auto mb-3" />
          <h1 className="text-2xl font-bold">{isFiltered ? 'Einverständnis-Erklärung' : 'Einverständnis-Erklärungen'}</h1>
          {data?.first_name && (
            <p className="text-white/60 text-sm mt-1">Hoi {data.first_name} 👋 — bitte fülle die Erklärung vor deinem Termin aus.</p>
          )}
          <p className="text-white/50 text-xs mt-1">
            {isFiltered
              ? 'Für deine gebuchte Leistung gesetzlich erforderlich — einmalig, dauert 1 Minute.'
              : 'Für Kryosauna & EMS-Training gesetzlich erforderlich — einmalig, dauert 1 Minute.'}
          </p>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-300 text-sm text-center" data-testid="consent-link-error">
            <AlertTriangle className="w-5 h-5 mx-auto mb-2" />
            {error}
          </div>
        )}

        {!error && !data && (
          <p className="text-center text-white/50 text-sm">Wird geladen...</p>
        )}

        {(visibleForms).map(form => (
          <div key={form.id} className="bg-white/5 border border-white/10 rounded-xl overflow-hidden" data-testid={`public-consent-form-${form.id}`}>
            <button
              type="button"
              className="w-full flex items-center justify-between px-4 py-3 text-left"
              onClick={() => !form.signed && setOpenFormId(openFormId === form.id ? null : form.id)}
              data-testid={`public-consent-toggle-${form.id}`}
            >
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate">{form.title}</p>
                {form.signed ? (
                  <p className="text-xs text-green-400 flex items-center gap-1 mt-0.5">
                    <CheckCircle2 className="w-3 h-3" />
                    {form.signed.signed_at && !isNaN(new Date(form.signed.signed_at))
                      ? `Unterschrieben am ${new Date(form.signed.signed_at).toLocaleDateString('de-CH')}`
                      : 'Unterschrieben'}
                  </p>
                ) : (
                  <p className="text-xs text-amber-400 flex items-center gap-1 mt-0.5">
                    <AlertTriangle className="w-3 h-3" /> Ausstehend — bitte ausfüllen
                  </p>
                )}
              </div>
              {form.signed
                ? <CheckCircle2 className="w-5 h-5 text-green-400 shrink-0" />
                : (openFormId === form.id ? <ChevronUp className="w-4 h-4 text-white/50 shrink-0" /> : <ChevronDown className="w-4 h-4 text-white/50 shrink-0" />)}
            </button>

            {!form.signed && openFormId === form.id && (
              <div className="px-4 pb-4 space-y-3 border-t border-white/10 pt-3">
                <ConsentSignSection form={form} onChange={setPayload} />
                <Button
                  onClick={() => submit(form.id)}
                  disabled={submitting}
                  className="w-full bg-yellow-500 hover:bg-yellow-400 text-black font-bold"
                  data-testid={`public-consent-submit-${form.id}`}
                >
                  {submitting ? 'Wird gespeichert...' : '✍️ Verbindlich unterschreiben'}
                </Button>
              </div>
            )}
          </div>
        ))}

        {data && visibleForms.length > 0 && visibleForms.every(f => f.signed) && (
          <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 text-green-300 text-sm text-center" data-testid="public-consent-all-done">
            🎉 Alles unterschrieben — wir freuen uns auf dein Training!
          </div>
        )}
      </div>
    </div>
  );
};

export default ConsentSignPage;

